import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AudioAvatarConfig } from "@models/avatar-config";
import { RootState } from "@store/store";
import Loader from "@components/Loader/Loader";
import { useRecordVoice } from "@hooks/useRecordVoice";
import styled from "@emotion/styled";
import { setInitializing, setReady, setTalking } from "@store/avatarSlice";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";
import { AudioAvatarService } from "@service/avatar/audio-avatar.service";
import { Box } from "@mui/material";
import { useDebounceProcessing } from "@hooks/useDebounceProcessing";
import { useSendAudioMessageMutation } from "@store/companionApi";
import { EventType } from "@models/audio-message";
import { pushToTalkEndStreamMessage } from "@models/audio-message-stream";
import {
  ConversationPacing,
  VoiceInputMode,
} from "@models/conversation-settings";

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  height: 100%;
  justify-content: center;
  color: white;
`;

const AvatarImage = styled.img`
  max-width: 300px;
  width: 100%;
`;

const AudioAvatar: FC<{
  sessionId: string;
  photoId?: string;
  voiceContextPhotoId?: string;
  avatarConfig: AudioAvatarConfig;
  streaming: boolean;
  conversationPacing: ConversationPacing;
  voiceInputMode: VoiceInputMode;
  pauseDurationMs: number;
  ttsEvictionMs: number;
}> = ({
  sessionId,
  photoId,
  voiceContextPhotoId,
  avatarConfig,
  streaming,
  conversationPacing,
  voiceInputMode,
  pauseDurationMs,
  ttsEvictionMs,
}) => {
  const avatarRef = useRef<AudioAvatarService | null>(null);
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const [text, setText] = useState("");
  const [sendAudioMessage] = useSendAudioMessageMutation();
  const { setValue: setProcessing } = useDebounceProcessing();
  const debouncedPhotoIdRef = useRef<string | undefined>();
  const voiceContextPhotoIdRef = useRef<string | undefined>();
  voiceContextPhotoIdRef.current = voiceContextPhotoId ?? photoId;
  const dispatch = useDispatch();
  const { vadBargeInEnabled } = useCompanionConversation();

  const speak = useCallback(
    async (newText: string) => {
      setProcessing(true, true);
      setTalking(true);
      console.log(text + newText);
      setText(text + newText);
      avatarRef.current!.speak(text + newText);
      setTalking(false);
      setProcessing(false, true);
    },
    [setProcessing, setText, text],
  );

  const { send } = useCompanionStreamingService(speak, true, ttsEvictionMs);

  const signalPushToTalkEnd = useCallback(async () => {
    if (voiceInputMode !== "push_to_talk") {
      return;
    }
    try {
      await send(
        pushToTalkEndStreamMessage({
          sessionId,
          photoId: voiceContextPhotoIdRef.current ?? photoId ?? null,
          conversationPacing,
          vadBargeInEnabled,
          voiceInputMode,
        }),
      );
    } catch (e) {
      console.error(e);
    }
  }, [
    conversationPacing,
    send,
    sessionId,
    vadBargeInEnabled,
    voiceInputMode,
    photoId,
  ]);

  const sendMessageStreaming = useCallback(
    async (
      audioBlob?: ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      try {
        setText((text) => text + "<br/>");
        await send({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
          conversationPacing,
          vadBargeInEnabled,
          voiceInputMode,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [conversationPacing, send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  const sendMessageBatching = useCallback(
    async (
      audioBlob?: Blob,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      try {
        const { data } = await sendAudioMessage({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
          conversationPacing,
        });

        if (!data) {
          return;
        }

        const { aiMessage } = data;

        if (!aiMessage) {
          return;
        }
        await speak(aiMessage);
      } catch (e) {
        console.error(e);
      }
    },
    [conversationPacing, sessionId, speak, sendAudioMessage],
  );

  const sendMessage = useCallback(
    async (
      audioBlob?: ArrayBufferLike | Blob,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      // Blobs only come from MediaRecorder (non-streaming or legacy). MicStream sends ArrayBufferLike → websocket.
      const shouldUseBatching = !streaming || audioBlob instanceof Blob;
      await (shouldUseBatching
        ? sendMessageBatching(audioBlob as Blob, photoId, eventType)
        : sendMessageStreaming(audioBlob as ArrayBufferLike, photoId, eventType));
    },
    [streaming, sendMessageStreaming, sendMessageBatching],
  );

  useRecordVoice(
    processing,
    (audio) =>
      sendMessage(audio, voiceContextPhotoIdRef.current, EventType.UserInput),
    streaming,
    voiceInputMode,
    pauseDurationMs,
    () => {
      void signalPushToTalkEnd();
    },
  );

  const startAvatar = async (avatar: AudioAvatarService) => {
    dispatch(setInitializing(true));
    avatar.start();
    await sendMessage(undefined, photoId, EventType.NewConversation);
    dispatch(setInitializing(false));
    dispatch(setReady(true));
  };

  useEffect(() => {
    if (
      photoId &&
      debouncedPhotoIdRef.current &&
      photoId !== debouncedPhotoIdRef.current
    ) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    debouncedPhotoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    setProcessing(true, true);
    const avatar = new AudioAvatarService();
    avatarRef.current = avatar;
    startAvatar(avatar);

    return () => {
      avatar.stop();
      dispatch(setReady(false));
    };
  }, [avatarConfig, setProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!avatarRef.current) {
    return <Loader />;
  }

  return (
    <AvatarContainer>
      <AvatarImage src={`${process.env.PUBLIC_URL}/images/avatar.png`} />
      <Box
        maxWidth="500px"
        marginTop="16px"
        dangerouslySetInnerHTML={{ __html: text }}
      ></Box>
    </AvatarContainer>
  );
};

export default memo(AudioAvatar);
